import { Controller } from '@hotwired/stimulus';

// Matched in document order, so the first hit is the first error. The last entry catches
// groups that only colour their hint.
const FIELD_IN_ERROR = [
  '.ui-field--error',
  '.ui-rte--error',
  '.ui-location-field--error',
  '.ui-plain-text-editor--error',
  '.ui-phone-number-field--error',
  '.ui-hint--error',
].join(', ');

// The header and stepper are both sticky, so the top of the viewport is not visible space.
const STICKY_CHROME = ['.ui-wizard-header--sticky', '.lj-post-wizard__stepper'];

// The footer is fixed over the content, so neither is the bottom.
const FIXED_FOOTER = '.ui-wizard-footer--fixed';

// A step that fails comes back rendered at the top, so an error further down goes unseen
// and Continue reads as having done nothing.
export default class extends Controller {
  connect() {
    const field = this.firstVisibleError();
    if (!field) return;

    // Fonts and the editor settle after connect and move things down the page.
    requestAnimationFrame(() => this.reveal(field));
  }

  // Collapsed panels carry message slots that match the selectors while showing nothing, and
  // they come first in document order. Scrolling to one moves the page nowhere and leaves the
  // real error where it was.
  firstVisibleError() {
    return [...this.element.querySelectorAll(FIELD_IN_ERROR)].find(field => field.getClientRects().length);
  }

  reveal(field) {
    if (this.alreadyInView(field)) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    field.scrollIntoView({ block: 'center', behavior: reduced ? 'auto' : 'smooth' });
  }

  alreadyInView(field) {
    const { top, bottom } = field.getBoundingClientRect();

    return top >= this.occludedTop() && bottom <= this.occludedBottom();
  }

  // The last field on a step sits under the footer, which covers it without pushing it out
  // of the viewport, so the bottom edge is the footer's top rather than the window's.
  occludedBottom() {
    const footer = document.querySelector(FIXED_FOOTER);

    return footer ? footer.getBoundingClientRect().top : window.innerHeight;
  }

  // Measured rather than assumed: the chrome is shorter on a narrow viewport.
  occludedTop() {
    return STICKY_CHROME.reduce((deepest, selector) => {
      const element = document.querySelector(selector);

      return element ? Math.max(deepest, element.getBoundingClientRect().bottom) : deepest;
    }, 0);
  }
}
